import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { getTagColor } from '../utils';

function EventList({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllShifts, setShowAllShifts] = useState(false);

  // Check if user has helferpool tags (excluding "Helferpool befreit")
  const userTags = user?.helferpool
    ? user.helferpool.split(/;\s*/).map(t => t.trim()).filter(t => t)
    : [];
  const userHasRelevantTags = userTags.filter(t => t.toLowerCase() !== 'helferpool befreit').length > 0;

  useEffect(() => {
    loadEvents();
  }, []);

  // Helper function to check if a shift should be visible to the user
  const isShiftVisibleToUser = (shift) => {
    // If "show all" is enabled, show everything
    if (showAllShifts) {
      return true;
    }

    // Get user's helferpool tags (excluding "Helferpool befreit")
    const relevantUserTags = userTags
      .map(t => t.toLowerCase())
      .filter(t => t && t !== 'helferpool befreit');

    // If user has no relevant tags, show all shifts
    if (relevantUserTags.length === 0) {
      return true;
    }

    // Get shift's tags
    const shiftTags = shift.tags
      ? shift.tags.split(/;\s*/).map(t => t.trim().toLowerCase()).filter(t => t)
      : [];

    // If shift has no tags, show to everyone
    if (shiftTags.length === 0) {
      return true;
    }

    // Check if any user tag matches any shift tag
    return relevantUserTags.some(userTag => shiftTags.includes(userTag));
  };

  const loadEvents = async () => {
    try {
      const res = await api.get('/events');
      setEvents(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) return <div>Lädt Events...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ margin: 0 }}>Anstehende Events</h2>
        {userHasRelevantTags && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input
              type="checkbox"
              checked={showAllShifts}
              onChange={(e) => setShowAllShifts(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Alle Einsätze anzeigen
          </label>
        )}
      </div>

      {events.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666' }}>
            Noch keine Events vorhanden.
          </p>
        </div>
      ) : (
        events.map(event => {
          // Filter shifts based on user's helferpool tags
          const visibleShifts = event.shifts?.filter(isShiftVisibleToUser) || [];
          const totalHelpers = visibleShifts.reduce((sum, s) => sum + (s.helpers_count || 0), 0);
          const totalNeeded = visibleShifts.reduce((sum, s) => sum + s.helpers_needed, 0);
          const isFull = totalHelpers >= totalNeeded;

          // Skip events with no visible shifts
          if (visibleShifts.length === 0) {
            return null;
          }

          return (
            <Link key={event.id} to={`/event/${event.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="card" style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <h3>{event.title}</h3>
                    <p style={{ color: '#666', margin: '0.5rem 0' }}>{formatDate(event.date)}</p>
                    <p style={{ color: '#666', marginBottom: '0.5rem' }}>📍 {event.location}</p>
                    {event.games && event.games.length > 0 ? (
                      <div style={{ color: '#555', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                        <strong>Spiele:</strong>
                        {event.games.slice(0, 3).map((game, idx) => (
                          <div key={idx} style={{ marginTop: '0.25rem' }}>
                            {game.game_time} {game.league_short ? `[${game.league_short}]` : ''} {game.team_a} vs {game.team_b}
                          </div>
                        ))}
                        {event.games.length > 3 && (
                          <div style={{ color: '#888', fontStyle: 'italic' }}>
                            + {event.games.length - 3} weitere Spiele
                          </div>
                        )}
                      </div>
                    ) : event.description ? (
                      <p style={{ color: '#888', marginTop: '0.5rem', whiteSpace: 'pre-line' }}>
                        {event.description.substring(0, 100)}{event.description.length > 100 ? '...' : ''}
                      </p>
                    ) : null}

                    {visibleShifts.length > 0 && (
                      <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                        <strong>{visibleShifts.length} Einsätze:</strong>
                        <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {visibleShifts.map(shift => (
                            <div key={shift.id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: shift.helpers_count >= shift.helpers_needed ? '#e8f5e9' : '#fff3e0',
                                borderRadius: '4px',
                                fontSize: '0.85rem'
                              }}>
                                {shift.name} ({shift.start_time}-{shift.end_time}): {shift.helpers_count}/{shift.helpers_needed}
                              </span>
                              {shift.tags && shift.tags.split(/;\s*/).map((tag, idx) => {
                                const trimmedTag = tag.trim();
                                if (!trimmedTag) return null;
                                const color = getTagColor(trimmedTag);
                                return (
                                  <span key={idx} style={{
                                    padding: '0.1rem 0.4rem',
                                    borderRadius: '8px',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    backgroundColor: color.bg,
                                    color: color.text
                                  }}>
                                    {trimmedTag}
                                  </span>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: isFull ? '#e8f5e9' : '#fff3e0',
                    borderRadius: '4px',
                    textAlign: 'center',
                    height: 'fit-content',
                    marginLeft: '1rem'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {totalHelpers}/{totalNeeded}
                    </div>
                    <div style={{ fontSize: '0.8rem' }}>Helfer</div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })
      )}
    </div>
  );
}

export default EventList;
